import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DuplicateCustomerModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  customer,
  isLoading = false
}) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (isOpen && customer) {
      // Pre-fill with original email
      setEmail(customer?.email || '');
      setEmailError('');
    }
  }, [isOpen, customer]);

  const handleClose = () => {
    if (!isLoading) {
      setEmail('');
      setEmailError('');
      onClose();
    }
  };

  const validateEmail = (emailValue) => {
    if (!emailValue || emailValue?.trim() === '') {
      setEmailError('Email is required');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(emailValue)) {
      setEmailError('Please enter a valid email address');
      return false;
    }

    // Check if email is different from original
    if (emailValue === customer?.email) {
      setEmailError('Please enter a different email address to create a copy');
      return false;
    }

    setEmailError('');
    return true;
  };

  const handleEmailChange = (e) => {
    const newEmail = e?.target?.value;
    setEmail(newEmail);
    if (emailError) {
      validateEmail(newEmail);
    }
  };

  const handleSubmit = () => {
    if (validateEmail(email)) {
      onConfirm(email);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary/10">
              <Icon 
                name="Copy" 
                size={20} 
                className="text-primary" 
              />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground">Duplicate Customer</h3>
              <p className="text-sm text-muted-foreground">Copy customer record</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              disabled={isLoading}
              className="h-8 w-8"
            >
              <Icon name="X" size={16} />
            </Button>
          </div>

          {/* Body */}
          <div className="space-y-4">
            <div className="bg-info/10 border border-info/20 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-info flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-foreground font-medium mb-1">Important Notice</p>
                  <p className="text-sm text-muted-foreground">
                    To create a copy of this customer, you must provide a unique email address. 
                    The email address must be different from the original customer's email.
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Info */}
            {customer && (
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Original Customer</p>
                <p className="text-sm text-foreground font-medium">{customer?.customer_name || customer?.name}</p>
                <p className="text-xs text-muted-foreground mt-1">Email: {customer?.email}</p>
              </div>
            )}

            {/* Email Input */}
            <div className="space-y-2">
              <Input
                type="email"
                label="New Email Address"
                value={email}
                onChange={handleEmailChange}
                onBlur={() => validateEmail(email)}
                error={emailError}
                required
                placeholder="Enter a unique email address"
                disabled={isLoading}
                autoFocus
              />
              {!emailError && email && email !== customer?.email && (
                <p className="text-xs text-green-600 flex items-center space-x-1">
                  <Icon name="Check" size={12} />
                  <span>Email is valid and unique</span>
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSubmit}
              disabled={isLoading || !email || email === customer?.email || !!emailError}
              loading={isLoading}
              iconName={isLoading ? "Loader2" : "Copy"}
              iconPosition="left"
            >
              {isLoading ? 'Copying...' : 'Copy Customer'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateCustomerModal;

